import { useState } from 'react';
import { type OutputOf, sellerChecklist, suggestedCategories } from '@going-once/client';
import { api } from '../lib/api';
import { type AsyncState, describeError, useAsync } from '../lib/useAsync';
import { refreshSession } from '../lib/auth';
import { Card, Field, Pressable, Stack, Text } from '../ui/primitives';
import { ErrorNote } from '../ui/state';

type Checklist = OutputOf<'sellers.onboarding.checklist'>;

/**
 * Becoming a seller: waitlist → admission → phone → payouts.
 *
 * **The server decides the order** (D9). This screen renders whichever step comes back
 * marked `next` and does not compute one — a client that decided the sequence would be a
 * second implementation of the rule, and the one that disagrees is always the client.
 *
 * The two steps here are the ones with real external dependencies, and both fail in ways
 * worth showing honestly rather than hiding behind a spinner: an SMS that never arrives
 * (the account is in the SNS sandbox, so only verified numbers receive anything), and a
 * Stripe hand-off that leaves our app entirely and comes back by webhook.
 */

/** Where a person is in becoming a seller: not admitted yet, or working the checklist. */
export type SellerSetup = { kind: 'waitlist' } | { kind: 'steps'; checklist: Checklist };

/**
 * The seller's onboarding state — fetched here, **owned by the caller**.
 *
 * It is a hook rather than a fetch inside the screen because the caller has to answer
 * "is this person still onboarding?" before it can decide whether to render onboarding
 * at all, and that question has exactly one correct source: the server's checklist (D9).
 * The gate used to be `claims.sellerId`, which becomes true the moment staff admit
 * someone — so an admitted seller went straight to the listing screen with neither a
 * verified phone nor a Stripe account, and the two steps that create them had no route
 * to them. One query, one owner, and the client still computes no ordering of its own.
 */
export function useSellerSetup(signedIn: boolean): AsyncState<SellerSetup | undefined> {
  return useAsync(async (): Promise<SellerSetup | undefined> => {
    if (!signedIn) return undefined;
    try {
      // Asks Stripe first while payouts are next, for a verdict whose webhook never came.
      return { kind: 'steps', checklist: await sellerChecklist(api()) };
    } catch (caught) {
      // NOT_A_SELLER is the expected answer for someone who has not been admitted — an
      // error to the pipeline, an ordinary state to a person.
      if (describeError(caught).code === 'NOT_A_SELLER') return { kind: 'waitlist' };
      throw caught;
    }
  }, [signedIn]);
}

export function Onboarding({
  setup,
  onChanged,
  onBecameSeller,
}: {
  setup: SellerSetup;
  onChanged: () => void;
  onBecameSeller: () => void;
}) {
  if (setup.kind === 'waitlist') return <Waitlist />;
  return <Steps checklist={setup.checklist} onChanged={onChanged} onBecameSeller={onBecameSeller} />;
}

/** Not yet admitted. Joining grants nothing — admission is a staff action. */
function Waitlist() {
  const [note, setNote] = useState('');
  const [state, setState] = useState<'idle' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<ReturnType<typeof describeError>>();
  const taxonomy = useAsync(() => api().query('catalog.taxonomy.get', {}), []);

  if (state === 'sent') {
    return (
      <Card>
        <Stack gap="sm" align="start">
          <Text step="heading">You are on the list</Text>
          <Text tone="fg.muted">
            Admission is a manual step — someone reviews the queue. Nothing else to do here until then.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap="md" align="start">
        <Text step="heading">Ask to sell</Text>
        <Text tone="fg.muted">
          Being on the waitlist grants nothing on its own. Admission is a staff decision, and it is what creates the
          seller account.
        </Text>
        {error ? <ErrorNote error={error} /> : null}
        <Field
          id="waitlist-note"
          label="Anything worth knowing (optional)"
          value={note}
          onChange={setNote}
          multiline
          placeholder="What you sell, how much of it, where it comes from."
        />
        <Pressable
          variant="primary"
          busy={taxonomy.loading}
          onPress={() => {
            void api()
              .command(
                'sellers.waitlist.join',
                {
                  categoryIds: (taxonomy.data ? suggestedCategories(taxonomy.data) : [])
                    .slice(0, 3)
                    .map((entry) => entry.categoryId),
                  ...(note ? { note } : {}),
                },
                {},
              )
              .then(() => setState('sent'))
              .catch((caught: unknown) => {
                setError(describeError(caught));
                setState('error');
              });
          }}
        >
          Join the waitlist
        </Pressable>
      </Stack>
    </Card>
  );
}

const STEP_LABEL: Record<string, string> = {
  phone_verification: 'Verify a phone number',
  stripe_kyc: 'Set up payouts',
  storefront_profile: 'Name your storefront',
};

function Steps({
  checklist,
  onChanged,
  onBecameSeller,
}: {
  checklist: Checklist;
  onChanged: () => void;
  onBecameSeller: () => void;
}) {
  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text as="h1" step="display">
          Set up selling
        </Text>
        <Text step="label" tone="fg.muted">
          {checklist.canGoLive ? 'Everything is done — you can list and go live.' : 'Three steps, in this order.'}
        </Text>
      </Stack>

      {checklist.steps.map((step) => (
        <Card key={step.step}>
          <Stack gap="md">
            <Stack direction="row" justify="between" align="center">
              <Text step="heading" tone={step.done ? 'success' : 'fg.default'}>
                {step.done ? '✓ ' : ''}
                {STEP_LABEL[step.step] ?? step.step}
              </Text>
              {step.next ? (
                <Text step="label" tone="live">
                  Next
                </Text>
              ) : null}
            </Stack>

            {step.next && step.step === 'phone_verification' ? <PhoneStep onDone={onChanged} /> : null}
            {step.next && step.step === 'stripe_kyc' ? <PayoutsStep /> : null}
            {step.next && step.step === 'storefront_profile' ? <ProfileStep onDone={onBecameSeller} /> : null}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

function PhoneStep({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState('+1');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReturnType<typeof describeError>>();

  const run = (work: Promise<unknown>, after: () => void) => {
    setBusy(true);
    setError(undefined);
    void work
      .then(after)
      .catch((caught: unknown) => setError(describeError(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <Stack gap="md" align="start">
      <Text tone="fg.muted">
        Payouts need a verified number. It is never shown to buyers and never shared — we use it only to confirm this
        account is yours.
      </Text>
      {error ? <ErrorNote error={error} /> : null}

      {sent ? (
        <>
          <Field id="sms-code" label="Six-digit code" value={code} onChange={setCode} placeholder="000000" />
          <Stack direction="row" gap="sm">
            <Pressable
              variant="primary"
              busy={busy}
              onPress={() => run(api().command('sellers.phone.verify', { code }, {}), onDone)}
            >
              Verify
            </Pressable>
            <Pressable variant="quiet" onPress={() => setSent(false)}>
              Use a different number
            </Pressable>
          </Stack>
          {/* Worth saying plainly: in the SNS sandbox only verified destination numbers
              receive anything, and a silent non-delivery looks identical to a slow one. */}
          <Text step="label" tone="fg.muted">
            No message? In sandbox, only phone numbers verified in AWS receive SMS.
          </Text>
        </>
      ) : (
        <>
          <Field
            id="sms-phone"
            label="Phone number (E.164)"
            value={phone}
            onChange={setPhone}
            placeholder="+12025550123"
          />
          {/* **Carrier compliance, not decoration.** US toll-free registration requires a
              screenshot of the moment consent is given, and the reviewer compares it
              against the opt-in description we filed. Pressing the button below is the
              opt-in, so the sentence has to sit above the button and say what arrives.
              If this wording changes, the filed description changes with it. */}
          <Text step="label" tone="fg.muted">
            By tapping Send code you agree to receive a one-time verification code by SMS from Going Once. Message and
            data rates may apply. Reply STOP to opt out, HELP for help.
          </Text>
          <Pressable
            variant="primary"
            busy={busy}
            onPress={() => {
              setBusy(true);
              setError(undefined);
              void api()
                .command('sellers.phone.request', { phoneE164: phone }, {})
                .then(() => setSent(true))
                .catch((caught: unknown) => {
                  const described = describeError(caught);
                  setError(described);
                  /**
                   * **A failed send still offers the code field.** The challenge is
                   * written before the message goes out and deliberately survives a send
                   * failure, because the text may well have arrived anyway — SNS can
                   * report a failure on a message a carrier delivered. Stranding the
                   * seller on a screen with nowhere to type a code they are holding is
                   * the worse of the two wrong answers, and the error stays on screen
                   * above the field so nobody is told it worked.
                   */
                  if (described.code === 'SMS_SEND_FAILED') setSent(true);
                })
                .finally(() => setBusy(false));
            }}
          >
            Send code
          </Pressable>
        </>
      )}
    </Stack>
  );
}

function PayoutsStep() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReturnType<typeof describeError>>();

  return (
    <Stack gap="md" align="start">
      <Text tone="fg.muted">
        Payouts run through Stripe, which collects the identity and bank details directly — we never see them. You will
        leave this page and come back when it is done.
      </Text>
      {error ? <ErrorNote error={error} /> : null}
      <Pressable
        variant="primary"
        busy={busy}
        onPress={() => {
          setBusy(true);
          setError(undefined);
          void api()
            .command('sellers.kyc.start', {}, {})
            // A full navigation, not a popup: Stripe's hosted flow is a multi-page
            // journey and a blocked popup is a dead end with no error to show.
            .then((result) => window.location.assign(result.onboardingUrl))
            .catch((caught: unknown) => {
              setError(describeError(caught));
              setBusy(false);
            });
        }}
      >
        Continue to Stripe
      </Pressable>
      <Text step="label" tone="fg.muted">
        Approval arrives from Stripe, not from us — an account stays pending until they confirm it, which can be after
        you return.
      </Text>
    </Stack>
  );
}

function ProfileStep({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReturnType<typeof describeError>>();

  return (
    <Stack gap="md" align="start">
      <Text tone="fg.muted">This is the name buyers see. It can change later.</Text>
      {error ? <ErrorNote error={error} /> : null}
      <Field id="store-name" label="Storefront name" value={name} onChange={setName} />
      <Pressable
        variant="primary"
        busy={busy}
        onPress={() => {
          setBusy(true);
          void api()
            .command('sellers.profile.update', { displayName: name }, {})
            // The claims are stale the moment onboarding completes — a new token is the
            // only way `sellerId` and the seller scopes reach the client.
            .then(() => refreshSession())
            .then(onDone)
            .catch((caught: unknown) => {
              setError(describeError(caught));
              setBusy(false);
            });
        }}
      >
        Save and finish
      </Pressable>
    </Stack>
  );
}
